import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { LoyaltyCardSection } from '../loyalty/LoyaltyCardSection';
import { ClientFormModal } from './ClientFormModal';
import { ClientHistoryTab } from './ClientHistoryTab';
import { ClientMainNoteSection } from './ClientMainNoteSection';
import {
  downloadClientExport,
  useClientAnonymize,
  useClientDetail,
} from './api';
import { formatPhone } from './phone';

type Tab = 'data' | 'history';

export function ClientDetailPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? Number(idParam) : null;
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const canMutate = isAdmin || user?.role === 'RECEPTION';

  const { data: client, isLoading, error } = useClientDetail(id);
  const anonymize = useClientAnonymize(id ?? 0);

  const [tab, setTab] = useState<Tab>('data');
  const [editOpen, setEditOpen] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  if (id === null || Number.isNaN(id)) {
    return <div className="p-6 text-zinc-500">{t('clients.detail.invalidId')}</div>;
  }
  if (isLoading) {
    return <div className="p-6 text-zinc-400">{t('common.loading')}</div>;
  }
  if (error || !client) {
    return (
      <div className="p-6 text-red-600">
        {error ? extractApiError(error) : t('clients.detail.notFound')}
      </div>
    );
  }

  const isAnon = client.anonymizedAt !== null;

  const onAnonymize = async () => {
    setActionMsg(null);
    setActionErr(null);
    if (!window.confirm(t('clients.detail.anonymizeConfirm'))) return;
    try {
      await anonymize.mutateAsync();
      setActionMsg(t('clients.detail.anonymizeOk'));
    } catch (err) {
      setActionErr(extractApiError(err));
    }
  };

  const onExport = async () => {
    setActionMsg(null);
    setActionErr(null);
    setExporting(true);
    try {
      const data = await downloadClientExport(client.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `client-${client.id}-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionErr(extractApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const fullName =
    client.firstName + (client.lastName ? ` ${client.lastName}` : '');

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/clients"
            className="text-sm text-brand-600 hover:text-brand-700"
          >
            ← {t('clients.detail.back')}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-zinc-900">
            {fullName}
            {isAnon && (
              <span className="ml-2 rounded bg-zinc-200 px-2 py-0.5 text-sm text-zinc-600">
                {t('clients.anonymized')}
              </span>
            )}
          </h1>
        </div>
        {canMutate && !isAnon && (
          <button type="button" className="btn-primary" onClick={() => setEditOpen(true)}>
            {t('common.actions.edit')}
          </button>
        )}
      </header>

      <div className="border-b border-zinc-200">
        <nav className="-mb-px flex gap-4">
          <TabButton active={tab === 'data'} onClick={() => setTab('data')}>
            {t('clients.detail.tabs.data')}
          </TabButton>
          <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
            {t('clients.detail.tabs.history')}
          </TabButton>
        </nav>
      </div>

      {actionMsg && (
        <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200">
          {actionMsg}
        </div>
      )}
      {actionErr && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {actionErr}
        </div>
      )}

      {tab === 'data' ? (
        <div className="space-y-4">
          <div className="grid gap-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 sm:grid-cols-2">
            <Row label={t('clients.form.firstName')} value={client.firstName} />
            <Row label={t('clients.form.lastName')} value={client.lastName ?? '—'} />
            <Row
              label={t('clients.form.phone')}
              value={isAnon ? '—' : formatPhone(client.phone)}
            />
            <Row label={t('clients.form.email')} value={client.email ?? '—'} />
            <Row
              label={t('clients.form.rodoConsent')}
              value={
                client.rodoConsent
                  ? `${t('common.status.granted')}${client.rodoConsentAt ? ` (${new Date(client.rodoConsentAt).toLocaleDateString()})` : ''}`
                  : t('common.status.notGranted')
              }
            />
            <Row
              label={t('clients.form.marketingConsent')}
              value={
                client.marketingConsent
                  ? t('common.status.granted')
                  : t('common.status.notGranted')
              }
            />
          </div>

          <ClientMainNoteSection clientId={client.id} disabled={isAnon} />

          {!isAnon && <LoyaltyCardSection clientId={client.id} />}

          {isAdmin && (
            <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200">
              <h2 className="text-sm font-semibold text-zinc-700">
                {t('clients.detail.rodoTitle')}
              </h2>
              <p className="mt-1 text-xs text-zinc-500">
                {t('clients.detail.rodoSubtitle')}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void onExport()}
                  disabled={exporting}
                >
                  {exporting ? t('common.loading') : t('clients.detail.exportButton')}
                </button>
                {!isAnon && (
                  <button
                    type="button"
                    onClick={() => void onAnonymize()}
                    disabled={anonymize.isPending}
                    className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {anonymize.isPending
                      ? t('common.saving')
                      : t('clients.detail.anonymizeButton')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <ClientHistoryTab clientId={client.id} />
      )}

      <ClientFormModal
        open={editOpen}
        client={client}
        onClose={() => {
          setEditOpen(false);
          // Po anonimizacji nie ma sensu odświeżać, ale przy edycji trzymamy się tu;
          // useClientDetail invaliduje się przez setQueryData w useClientUpdate.
          if (isAnon) navigate('/clients');
        }}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-1 text-sm text-zinc-900">{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-3 py-2 text-sm font-medium transition ${
        active
          ? 'border-brand-500 text-brand-700'
          : 'border-transparent text-zinc-600 hover:text-zinc-900'
      }`}
    >
      {children}
    </button>
  );
}
